import React, { type FC, useState, useEffect } from 'react';

import Form from '../../components/Form';
import InputText from '../../components/InputText';
import { resetErrors } from '../../redux/actions/resetErrors';
import { type RootState } from '../../redux/store';

import t from '../../lib/translate';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  createStationNetworkConfig,
  getStationNetworkOptions,
} from '../../redux/actions/stationNetworkConfigs';
import { type NetworkOption } from 'src/types';
import InputSelect from '../../components/InputSelect';

interface Props {
  stationId: number;
  finished: () => void;
}

interface FormInput {
  alias: string;
  networkOption?: NetworkOption;
}

const INITIAL_STATE: FormInput = {
  alias: '',
  networkOption: undefined,
};

const NetworkForm: FC<Props> = ({ stationId, finished }) => {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector((state: RootState) => state.isLoading.primary);
  const error = useAppSelector((state: RootState) => state.station.networkConfigs.error);
  const options = useAppSelector((state: RootState) => state.station.networkConfigs.options);

  const [values, setValues] = useState<FormInput>(INITIAL_STATE);

  useEffect(() => {
    void dispatch(getStationNetworkOptions(stationId));
  }, [stationId]);

  const closeModalAndResetFormErrors = (): void => {
    void dispatch(resetErrors());
    finished();
  };

  const handleChangeAlias = (alias: string): void => {
    setValues({ ...values, alias });
  };

  const handleSelectNetwork = (networkOption: NetworkOption): void => {
    setValues({ ...values, networkOption });
  };

  const handleSubmit = (): void => {
    const createRequest = {
      alias: values.alias,
      label: values.networkOption?.label ?? '',
      externalId: values.networkOption?.externalId ?? '',
    };
    void dispatch(createStationNetworkConfig(stationId, createRequest));
  };

  return (
    <Form
      label={t('network.form.title')}
      isLoading={isLoading}
      error={error}
      enableSubmit={values.alias !== undefined && values.networkOption !== undefined}
      onClose={closeModalAndResetFormErrors}
      onSubmit={handleSubmit}>
      <InputText
        name='alias'
        label={t('network.form.alias')}
        value={values.alias}
        handleChange={(e) => {
          handleChangeAlias(e.target.value);
        }}
      />
      <InputSelect
        label={'network.form.network'}
        selecteKey={values.networkOption?.externalId}
        options={options.map((op) => ({
          value: op.externalId,
          label: op.label,
        }))}
        onChange={(opt) => {
          handleSelectNetwork({ externalId: opt.value, label: opt.label });
        }}
      />
    </Form>
  );
};

export default NetworkForm;
